import { styled } from '@rybr/styled-modules'
import { useLocale, useTranslations } from 'next-intl'
import React, { useMemo } from 'react'

import { Carousel } from '@ntc/components/Carousel'
import { SanityImage, SanityImageProps } from '@ntc/components/SanityImage'
import { FormattedColorTheme, FormattedComponentBackdrop } from '@ntc/lib/utils/sanity-utils'

import { PromoTag } from '@components/PromoTag'
import { BADGE } from '@constants/constants.product'
import { useHomePageContext } from '@context/HomePageContext'
import { UseConstructorRecommendationProps, useConstructorRecommendations } from '@hooks/useConstructorRecommendations'
import { analytics } from '@lib/analytics/analytics-click-events'
import { theme } from '@src/theme'
import { RECOMMENDATION_CAROUSEL_SECTION_LAYOUT } from '../../../page.data.constants'
import { ComponentBackdrop } from '../ComponentBackdrop'
import { LegalModalProps } from '../LegalModal'
import { ProductTile, ProductTileProps } from './ProductTile'

import styles from './ConstructorRecommendationCarousel.module.scss'

export type ConstructorRecommendationCarouselProps = {
  layout: string | null
  backdrop: FormattedComponentBackdrop
  colorTheme: FormattedColorTheme
  title: string | null
  link: {
    title: string | null
    url: string | null
  }
  configs: UseConstructorRecommendationProps
  promoOffers: Array<{
    title: string | null
    subText: string | null
    description: string | null
    image: SanityImageProps | null
    mobileImage: SanityImageProps | null
    link: {
      title: string | null
      url: string | null
    } | null
    valueTag: {
      title: string | null
      color: string | undefined | null
      backgroundColor: string | undefined | null
    } | null
    promoTag: {
      textBefore: string | null
      exponentLeft: string | null
      text: string | null
      exponentRight: string | null
      textAfter: string | null
    } | null
    legal: (LegalModalProps & { ctaText: string | null }) | null
  }>
}

// width = (100% - #gaps * gap-size)/# items to show
const calcWidth = (columnCount: number, gap: string) =>
  `calc((100% - ${Math.ceil(columnCount) - 1} * ${gap}) / ${columnCount})`

const MainContainer = styled.div(styles.MainContainer)
const HeaderContainer = styled.div(styles.HeaderContainer)
const Header = styled.div(styles.Header)
const Title = styled.h3<{ colorTheme: ConstructorRecommendationCarouselProps['colorTheme'] }>(
  styles.Title,
  ({ colorTheme }) => {
    return { color: colorTheme?.textColor ?? theme['color-primary'] }
  }
)
const Link = styled.a<{ colorTheme: ConstructorRecommendationCarouselProps['colorTheme'] }>(
  styles.Link,
  ({ colorTheme }) => {
    return { color: colorTheme?.textColor ?? theme['color-primary'] }
  }
)

const LoadingProductTile = styled.div(styles.LoadingProductTile)

const MainContent = styled.div<{
  colorTheme: ConstructorRecommendationCarouselProps['colorTheme']
  promoOffer?: ConstructorRecommendationCarouselProps['promoOffers'][number]
}>(styles.MainContent, ({ colorTheme, promoOffer }) => {
  const backgroundColorPrimary = promoOffer == null ? null : (colorTheme?.backgroundColor ?? theme['color-neutral-2'])

  return {
    backgroundColor: backgroundColorPrimary,
    padding: backgroundColorPrimary ? theme['size-5'] : null,
    borderRadius: backgroundColorPrimary ? theme['border-radius-md'] : null
  }
})

const Content = styled.div(styles.Content)

const PromoMinimalMainContainer = styled.div(styles.PromoMinimalMainContainer)
const PromoMinimalTitle = styled.h3<{ colorTheme: ConstructorRecommendationCarouselProps['colorTheme'] }>(
  styles.PromoMinimalTitle,
  ({ colorTheme }) => ({
    color: colorTheme?.textColor ?? theme['color-primary']
  })
)
const PromoMinimalSubText = styled.p(styles.PromoMinimalSubText)
const PromoMinimalLink = styled.a<{ colorTheme: ConstructorRecommendationCarouselProps['colorTheme'] }>(
  styles.PromoMinimalLink,
  ({ colorTheme }) => ({
    color: colorTheme?.ctaTextColor ?? colorTheme?.accentColor ?? theme['color-secondary']
  })
)

const PromoHighlightMainContainer = styled.div(styles.PromoHighlightMainContainer)
const PromoHighlightContainer = styled.div(styles.PromoHighlightContainer)
const PromoMinimalContainer = styled.div(styles.PromoMinimalContainer)
const PromoHighlightSubText = styled.p<{ colorTheme: ConstructorRecommendationCarouselProps['colorTheme'] }>(
  styles.PromoHighlightSubText,
  ({ colorTheme }) => ({
    color: colorTheme?.textColor
  })
)
const PromoHighlightDescription = styled.p<{ colorTheme: ConstructorRecommendationCarouselProps['colorTheme'] }>(
  styles.PromoHighlightDescription,
  ({ colorTheme }) => ({
    color: colorTheme?.textColor
  })
)
const PromoHighlightValueTag = styled.p<{
  backgroundColor: string | undefined | null
  color: string | undefined | null
}>(styles.PromoHighlightValueTag, ({ backgroundColor, color }) => ({
  backgroundColor,
  color
}))
const PromoHighlightTitle = styled.h3<{ colorTheme: ConstructorRecommendationCarouselProps['colorTheme'] }>(
  styles.PromoHighlightTitle,
  ({ colorTheme }) => ({
    color: colorTheme?.textColor
  })
)

const CtaLink = styled.a<{ colorTheme: ConstructorRecommendationCarouselProps['colorTheme'] }>(
  styles.CtaLink,
  ({ colorTheme }) => ({
    color: colorTheme?.ctaTextColor,
    backgroundColor: colorTheme?.ctaColor
  })
)

const CarouselContainer = styled.div<{ columnCount: number; maxColumns: number }>(
  styles.CarouselContainer,
  ({ columnCount, maxColumns }) => {
    return {
      width: calcWidth(columnCount > maxColumns ? maxColumns + 0.3 : maxColumns, theme['size-4'])
    }
  }
)

const LegalModalButton = styled.button(styles.LegalLink)

const PromoOfferMinimal: React.FC<{
  promoOffer?: ConstructorRecommendationCarouselProps['promoOffers'][number] | null
  colorTheme: ConstructorRecommendationCarouselProps['colorTheme']
}> = ({ colorTheme, promoOffer }) => {
  return promoOffer == null ? null : (
    <PromoMinimalMainContainer>
      <PromoMinimalContainer>
        <PromoMinimalTitle $colorTheme={colorTheme}>{promoOffer.title}</PromoMinimalTitle>
        <PromoMinimalSubText>{promoOffer.subText}</PromoMinimalSubText>
        <PromoMinimalLink
          $colorTheme={colorTheme}
          href={promoOffer.link?.url ?? '#'}
        >
          {promoOffer.link?.title}
        </PromoMinimalLink>
      </PromoMinimalContainer>
      <PromoMinimalContainer>
        {promoOffer?.promoTag ? (
          <PromoTag
            colorTheme={colorTheme}
            promoTag={promoOffer?.promoTag}
            textBeforeStyle={styles.PromoTagTextBeforeAndAfter}
            exponentLeftStyle={styles.PromoTagExponent}
            textStyle={styles.PromoTagText}
            exponentRightStyle={styles.PromoTagExponent}
            textAfterStyle={styles.PromoTagTextBeforeAndAfter}
          />
        ) : null}
      </PromoMinimalContainer>
    </PromoMinimalMainContainer>
  )
}

const PromoOfferHighlight: React.FC<{
  promoOffer?: ConstructorRecommendationCarouselProps['promoOffers'][number] | null
  colorTheme: ConstructorRecommendationCarouselProps['colorTheme']
}> = ({ colorTheme, promoOffer }) => {
  const { image, mobileImage, promoTag, subText, valueTag, description, link, title } = promoOffer ?? {}

  return promoOffer == null ? null : (
    <PromoHighlightMainContainer>
      {subText == null && valueTag == null && image == null ? null : (
        <PromoHighlightContainer>
          {subText == null ? null : <PromoHighlightSubText $colorTheme={colorTheme}>{subText}</PromoHighlightSubText>}
          {valueTag == null ? null : (
            <PromoHighlightValueTag
              $backgroundColor={valueTag.backgroundColor}
              $color={valueTag.color}
            >
              {valueTag.title}
            </PromoHighlightValueTag>
          )}
          {image == null ? null : (
            <SanityImage
              className={styles.ImageDesktop}
              {...image}
            />
          )}
          {mobileImage == null ? null : (
            <SanityImage
              className={styles.ImageMobile}
              {...mobileImage}
            />
          )}
        </PromoHighlightContainer>
      )}

      {promoTag == null ? null : (
        <PromoHighlightContainer>
          <PromoTag
            colorTheme={colorTheme}
            promoTag={promoTag}
            textBeforeStyle={styles.PromoTagTextBeforeAndAfter}
            exponentLeftStyle={styles.PromoTagExponent}
            textStyle={styles.PromoTagText}
            exponentRightStyle={styles.PromoTagExponent}
            textAfterStyle={styles.PromoTagTextBeforeAndAfter}
          />
        </PromoHighlightContainer>
      )}
      {title == null && description == null ? null : (
        <PromoHighlightContainer>
          {title == null ? null : <PromoHighlightTitle $colorTheme={colorTheme}>{title}</PromoHighlightTitle>}
          {description == null ? null : (
            <PromoHighlightDescription $colorTheme={colorTheme}>{description}</PromoHighlightDescription>
          )}
        </PromoHighlightContainer>
      )}
      {link?.title == null ? null : (
        <PromoHighlightContainer>
          <CtaLink
            href={link?.url ?? '#'}
            $colorTheme={colorTheme}
          >
            {link.title}
          </CtaLink>
        </PromoHighlightContainer>
      )}
    </PromoHighlightMainContainer>
  )
}

const loadingProductTiles = new Array(4).fill(null).map((_, i) => <LoadingProductTile key={i} />)

export const ConstructorRecommendationCarousel: React.FC<ConstructorRecommendationCarouselProps> = ({
  layout,
  backdrop,
  colorTheme,
  title,
  link,
  configs,
  promoOffers
}) => {
  const t = useTranslations()
  const locale = useLocale()
  const HomePageContext = useHomePageContext()
  const { data, isLoading } = useConstructorRecommendations(configs)

  const promoOffer = useMemo(() => promoOffers.find((promo) => !!promo), [promoOffers])
  const maxColumns = useMemo(() => (promoOffer ? 3 : 4), [promoOffer])

  const productTileProps = useMemo(
    () =>
      (data?.items ?? []).map<
        ProductTileProps & {
          id: string
          'data-cnstrc-item'?: string
          'data-cnstrc-item-id'?: string
          'data-cnstrc-item-name'?: string
          'data-cnstrc-strategy-id'?: string
        }
      >((product, index) => ({
        id: product.productId,
        url: product.url,
        image: {
          src: product.imageSrc,
          width: 284,
          height: 284
        },
        brandName: product.brandName,
        description: product.description,
        listPrice: product.originalPrice,
        salePrice: product.mainPrice,
        badge: product.badge,
        position: `1*${index + 1}`,
        'data-cnstrc-item': 'recommendation',
        'data-cnstrc-item-id': product.productId,
        'data-cnstrc-item-name': product.description,
        'data-cnstrc-strategy-id': product.strategyId,
        onClick: () =>
          analytics.carouselProductClick({
            articleNumber: product.productId,
            productID: product.omniItemId ?? '',
            manufacturerNumber: product.modelId ?? '',
            itemNumber: product.mmsItemNumber ?? '',
            brand: product.brandName ?? '',
            description: product.description ?? '',
            productSellingPrice:
              (product.originalPrice === product.mainPrice ? product.originalPrice : product.mainPrice) ?? 0,
            productWasPrice: product.originalPrice ?? 0,
            priceType: product.badge === BADGE.clearance ? 'L' : product.badge === BADGE.priceDrop ? 'P' : '',
            carouselName: title ?? '',
            position: `1*${index + 1}`
          })
      })),
    [data?.items]
  )

  const columnCount = useMemo(
    () => (isLoading ? loadingProductTiles.length : (productTileProps?.length ?? 0)),
    [productTileProps, isLoading]
  )

  return (
    <MainContainer
      data-cnstrc-recommendations
      data-cnstrc-recommendations-pod-id={configs.podId?.[locale]}
      data-cnstrc-num-results={productTileProps.length}
      data-cnstrc-result-id={data?.resultId}
    >
      <ComponentBackdrop backdrop={backdrop} />
      <MainContent
        $colorTheme={colorTheme}
        $promoOffer={promoOffer}
      >
        <HeaderContainer>
          <Header>
            {title ? <Title $colorTheme={colorTheme}>{title}</Title> : null}
            {link.title && link.url ? (
              <Link
                $colorTheme={colorTheme}
                href={link.url}
              >
                {link.title}
              </Link>
            ) : null}
          </Header>
        </HeaderContainer>
        <Content>
          {layout === RECOMMENDATION_CAROUSEL_SECTION_LAYOUT.minimal ? (
            <PromoOfferMinimal
              promoOffer={promoOffer}
              colorTheme={colorTheme}
            />
          ) : layout === RECOMMENDATION_CAROUSEL_SECTION_LAYOUT.highlight ? (
            <PromoOfferHighlight
              promoOffer={promoOffer}
              colorTheme={colorTheme}
            />
          ) : null}
          <CarouselContainer
            $columnCount={columnCount}
            $maxColumns={maxColumns}
          >
            <Carousel
              indexes={() => null}
              mainContainerStyle={styles.Carousel}
              leftArrowStyle={styles.CarouselLeftArrow}
              rightArrowStyle={styles.CarouselRightArrow}
              slideStyle={styles.CarouselSlide}
              slidesContainerStyle={styles.CarouselSlideContainer}
              onArrowClick={({ isLeft }) =>
                isLeft
                  ? analytics.carouselLeftClick({ cursorLinkType: 'Arrow Left Click', cursorLinkName: title ?? '' })
                  : analytics.carouselRightClick({ cursorLinkType: 'Arrow Right Click', cursorLinkName: title ?? '' })
              }
            >
              {isLoading
                ? loadingProductTiles
                : productTileProps.map((props, i) => (
                    <ProductTile
                      {...props}
                      key={`${i}-${props.id}`}
                    />
                  ))}
            </Carousel>
          </CarouselContainer>
        </Content>
      </MainContent>
      {promoOffer?.legal ? (
        <LegalModalButton onClick={() => HomePageContext.actions.setLegalModal({ isOpen: true, ...promoOffer.legal })}>
          {promoOffer.legal.ctaText ?? t('page.home.legalModal-details-button')}
        </LegalModalButton>
      ) : null}
    </MainContainer>
  )
}
